import * as React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import Backdrop from "../atoms/Backdrop";
import { CrossIcon } from "../atoms/icons/CrossIcon";
import { theme } from "../../theme";
import { HorizontalSpacer } from "../atoms/Spacers";
import Divider from "../atoms/Divider";
import DefaultButton from "../atoms/buttons/DefaultButton";
import OutlinedButton from "../atoms/buttons/OutlinedButton";
import ActionButton from "../atoms/buttons/ActionButton";
import TitleSmall from "../atoms/typography/TitleSmall";

interface ActionButtonProps {
  buttonTitle: string;
  disabled?: boolean;
  onClick: (e?: any) => void;
  icon?: React.ReactNode;
  loading?: boolean;
}

interface ModalProps {
  children: React.ReactNode;
  onClose: () => void;
  closeIcon?: boolean;
  backdropColor?: string;
  fixedWidth?: boolean;
  height?: number;
  width?: number;
  actionButtons?: {
    defaultButton?: ActionButtonProps;
    outlinedButton?: ActionButtonProps;
    actionButton?: ActionButtonProps;
    deleteButton?: ActionButtonProps;
  };
  title?: string;
}

const CrossIconSection = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const CrossIconButton = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  :hover {
    filter: brightness(40%);
  }
`;

const ModalStyle = styled(motion.div)<{
  fixedWidth?: boolean;
  height?: number;
  width?: number;
}>`
  background-color: white;
  margin: 15% auto; /* 15% from the top and centered */
  padding: 32px;
  border-radius: 12px;
  ${({ fixedWidth }) => fixedWidth && "width: 50%;"}
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  height: ${({ height }) => height ?? "auto"};
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: auto;
  width: ${({ width }) => (width ? `${width}px` : "auto")};
  max-width: 90vw;
`;

const dropIn = {
  hidden: {
    y: "-100%",
    opacity: 0,
  },
  visible: { y: 0, opacity: 1 },
  exit: {
    y: "100%",
    opacity: 0,
  },
};

const Modal: React.FunctionComponent<ModalProps> = ({
  children,
  onClose,
  closeIcon,
  backdropColor,
  fixedWidth = true,
  height,
  width,
  actionButtons,
  title,
}) => {
  return (
    <>
      <Backdrop onClick={onClose} backdropcolor={backdropColor ?? "#0000007f"}>
        <ModalStyle
          onClick={(e) => e.stopPropagation()}
          variants={dropIn}
          initial="hidden"
          animate="visible"
          exit="exit"
          fixedWidth={fixedWidth}
          height={height}
          width={width}
        >
          {closeIcon && (
            <CrossIconSection id="close">
              <CrossIconButton onClick={onClose}>
                <CrossIcon color={theme.colors.text} />
              </CrossIconButton>
            </CrossIconSection>
          )}
          {title && (
            <TitleSmall
              color={theme.colors.primary}
              textalign="center"
              style={{ marginBottom: 24 }}
            >
              {title}
            </TitleSmall>
          )}
          {children}
          {actionButtons && (
            <div>
              <HorizontalSpacer spacing={1.5} />
              <Divider />
              <HorizontalSpacer spacing={1} />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 24,
                }}
              >
                {actionButtons.defaultButton && (
                  <DefaultButton
                    title={actionButtons.defaultButton.buttonTitle}
                    disabled={actionButtons.defaultButton.disabled}
                    onClick={actionButtons.defaultButton.onClick}
                    icon={actionButtons.defaultButton.icon}
                  />
                )}
                {actionButtons.outlinedButton && (
                  <OutlinedButton
                    title={actionButtons.outlinedButton.buttonTitle}
                    onClick={actionButtons.outlinedButton.onClick}
                    disabled={actionButtons.outlinedButton.disabled}
                    medium
                    color={theme.colors.primary}
                    icon={actionButtons.outlinedButton.icon}
                    loading={actionButtons.outlinedButton.loading}
                  />
                )}
                {actionButtons.actionButton && (
                  <ActionButton
                    title={actionButtons.actionButton.buttonTitle}
                    onClick={actionButtons.actionButton.onClick}
                    disabled={actionButtons.actionButton.disabled}
                    color={theme.colors.primary}
                    icon={actionButtons.actionButton.icon}
                    loading={actionButtons.actionButton.loading}
                  />
                )}
                {actionButtons.deleteButton && (
                  <ActionButton
                    title={actionButtons.deleteButton.buttonTitle}
                    onClick={actionButtons.deleteButton.onClick}
                    disabled={actionButtons.deleteButton.disabled}
                    color={theme.colors.error}
                    icon={actionButtons.deleteButton.icon}
                    loading={actionButtons.deleteButton.loading}
                  />
                )}
              </div>
            </div>
          )}
        </ModalStyle>
      </Backdrop>
    </>
  );
};

export default Modal;
